import React, { Component } from "react";
import Box from "@material-ui/core/Box";
import Fade from "@material-ui/core/Fade";
import Button from "@material-ui/core/Button";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import mergeAdvanced from "object-merge-advanced";
import withStyles from "@material-ui/core/styles/withStyles";
import { connect, validateYupSchema, yupToFormErrors } from "formik";
import Async from "react-async";
import Form from "../form/Form";
import PropertySection from "./PropertySection";
import ResetButton from "../form/ResetButton";
import SubmitButton from "../form/SubmitButton";
import memoAssign from "../../util/memoAssign";

const propertyFormStyles = theme => ({
  paper: {
    padding: theme.spacing(2)
  },
  formSection: {
    marginBottom: theme.spacing(2)
  },
  divider: {
    marginBottom: theme.spacing(2)
  }
});

const NoComp = ({ children }) => children;

const PropertyFormInner = connect(
  class PropertyFormInner extends Component {
    shouldComponentUpdate = nextProps =>
      nextProps.formik.status !== this.props.formik.status ||
      nextProps.formik.values !== this.props.formik.values ||
      nextProps.formik.errors !== this.props.formik.errors ||
      nextProps.formik.touched !== this.props.formik.touched ||
      nextProps.fieldInfo !== this.props.fieldInfo ||
      nextProps.sections !== this.props.sections ||
      nextProps.hideCancel !== this.props.hideCancel ||
      nextProps.hideReset !== this.props.hideReset ||
      nextProps.hideSave !== this.props.hideSave ||
      nextProps.cancelText !== this.props.cancelText ||
      nextProps.resetText !== this.props.resetText ||
      nextProps.saveText !== this.props.saveText;

    getPropertyValue = property =>
      typeof property === "function"
        ? property(this.props.formik.values, this.props.formik)
        : property;

    render = () => {
      const {
        fieldInfo,
        sections = [],
        header,
        section,
        field,
        hideCancel,
        hideReset,
        hideSave,
        onCancel,
        cancelText,
        resetText,
        saveText,
        Wrapper = NoComp,
        ToolbarWrapper = Toolbar
      } = this.props;

      const propSections = sections.map(
        (
          {
            hidden,
            box = {},
            field: sectionField = {},
            header: sectionHeader = {},
            title,
            ...sectionEntry
          },
          j
        ) => (
          <PropertySection
            shouldHide={this.getPropertyValue(hidden)}
            title={this.getPropertyValue(title)}
            fieldInfo={fieldInfo}
            {...sectionEntry}
            header={memoAssign(header, sectionHeader)}
            field={memoAssign(field, sectionField)}
            section={memoAssign(section, box)}
            key={j++}
          />
        )
      );

      const hiddenCancel = this.getPropertyValue(hideCancel);
      const hiddenReset = this.getPropertyValue(hideReset);
      const hiddenSave = this.getPropertyValue(hideSave);
      const cancelledText = this.getPropertyValue(cancelText);
      const resettedText = this.getPropertyValue(resetText);
      const savedText = this.getPropertyValue(saveText);

      const toolbar = (!hiddenCancel || !hiddenReset || !hiddenSave) && (
        <ToolbarWrapper>
          {!hiddenCancel && <Button onClick={onCancel}>{cancelledText}</Button>}
          {!hiddenReset && <ResetButton>{resettedText}</ResetButton>}
          {!hiddenSave && <SubmitButton>{savedText}</SubmitButton>}
        </ToolbarWrapper>
      );

      return (
        <>
          <Wrapper>{propSections}</Wrapper>
          {toolbar}
        </>
      );
    };
  }
);

const loadAndMergeData = ({ initialValues, loadData }, { signal }) =>
  loadData
    ? Promise.resolve(loadData(initialValues, { signal })).then(result =>
        mergeAdvanced(initialValues || {}, result || {})
      )
    : Promise.resolve(initialValues || {});

const PropertyForm = withStyles(propertyFormStyles, { name: "LcPropertyForm" })(
  ({
    title,
    classes,
    initialValues,
    isInitialValid,
    validationSchema,
    fieldInfo,
    sections,
    resetText = "Reset",
    saveText = "Save",
    cancelText = "Cancel",
    onSubmit = (_, form) => form.setSubmitting(false),
    onCancel,
    hideCancel,
    hideReset,
    hideSave,
    hideTitle,
    titleTypographyProps = {},
    header,
    section,
    field,
    loadData,
    asyncLoading,
    asyncError,
    children,
    noFormTag,
    TransitionComponent = Fade,
    transitionProps = { in: true },
    Wrapper,
    ToolbarWrapper
  }) => (
    <Async
      promiseFn={loadAndMergeData}
      initialValues={initialValues}
      loadData={loadData}
      watch={[initialValues, loadData]}
    >
      <Async.Loading>{asyncLoading}</Async.Loading>
      <Async.Rejected>
        {asyncError || (error => <Box m={1}>{error.message}</Box>)}
      </Async.Rejected>
      <Async.Resolved persist>
        {data => (
          <TransitionComponent {...transitionProps}>
            <div>
              {!hideTitle && title && (
                <Typography
                  className={classes.divider}
                  variant="h5"
                  {...titleTypographyProps}
                >
                  {title}
                </Typography>
              )}
              <Form
                noFormTag={noFormTag}
                initialValues={data || initialValues}
                isInitialValid={isInitialValid}
                onSubmit={onSubmit}
                validate={
                  validationSchema &&
                  (values => {
                    try {
                      validateYupSchema(values, validationSchema, true, values);
                    } catch (err) {
                      return yupToFormErrors(err);
                    }

                    return {};
                  })
                }
              >
                <PropertyFormInner
                  fieldInfo={fieldInfo}
                  sections={sections}
                  header={header}
                  section={section}
                  field={field}
                  hideCancel={hideCancel}
                  hideReset={hideReset}
                  hideSave={hideSave}
                  onCancel={onCancel}
                  cancelText={cancelText}
                  resetText={resetText}
                  saveText={saveText}
                  Wrapper={Wrapper}
                  ToolbarWrapper={ToolbarWrapper}
                />

                {children}
              </Form>
            </div>
          </TransitionComponent>
        )}
      </Async.Resolved>
    </Async>
  )
);

export default PropertyForm;
